import { useMemo, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Disclosure,
  DisclosureContent,
  DisclosureTrigger,
  Field,
  Flex,
  Text,
} from '@optiaxiom/react';
import { IconAgent, IconXmark } from '@optimizely/axiom-icons';

import { ArtifactContainer, ArtifactProvider, Artifacts } from '@frontend/components/Artifact';
import { MarkdownText } from '@frontend/components/MarkdownText';
import { useInstanceId, useScrollToSection } from '@frontend/hooks';
import { SpecializedAgentExecution } from '@frontend/types/specialized-agent';

import {
  useGetAgentMemoryChunks,
  useGetSpecializedAgent,
  useGetSpecializedAgentExecution,
} from '../../../hooks/useSpecializedAgents';
import { StopExecutionButton } from '../../shared/StopExecutionButton';
import { TokenUsageSection } from '../../shared/TokenUsageSection';
import { EmptyStateMessage } from '../shared/EmptyStateMessage';
import { ExecutionDetailsCard } from '../shared/ExecutionDetailsCard';
import { ExecutionOutputDisplay } from '../shared/ExecutionOutputDisplay';
import { ExecutionStatusBadge } from '../shared/ExecutionStatusBadge';
import { MemoryChunksDisplay } from '../shared/MemoryChunksDisplay';

import { styles } from './SpecializedAgentExecutionDetailsSidebar.css';

type SpecializedAgentExecutionDetailsSidebarProps = {
  executionId?: string;
  execution?: SpecializedAgentExecution;
  onClose: () => void;
};

export function SpecializedAgentExecutionDetailsSidebar({
  executionId,
  execution: providedExecution,
  onClose,
}: SpecializedAgentExecutionDetailsSidebarProps) {
  const { instanceId } = useInstanceId();
  const [qualityScoreOpen, setQualityScoreOpen] = useState(false);
  const [tokenUsageOpen, setTokenUsageOpen] = useState(false);
  const qualityScoreRef = useRef<HTMLDivElement>(null);
  const tokenUsageRef = useRef<HTMLDivElement>(null);

  const sectionControls = useMemo(
    () => ({
      'quality-score-section': { ref: qualityScoreRef, setOpen: setQualityScoreOpen },
      'token-usage-section': { ref: tokenUsageRef, setOpen: setTokenUsageOpen },
    }),
    []
  );
  const handleNavigate = useScrollToSection(sectionControls);

  // Fetch execution details only if no execution is provided and executionId exists
  const shouldFetchExecution = !providedExecution && !!executionId && !!instanceId;
  const {
    execution: fetchedExecution,
    isPending: isLoadingExecution,
    error: executionError,
  } = useGetSpecializedAgentExecution(
    shouldFetchExecution ? instanceId : '',
    shouldFetchExecution ? executionId : ''
  );

  const execution = providedExecution || fetchedExecution;
  const isStreaming = execution
    ? execution?.status.toLowerCase() !== 'completed' &&
      execution?.status.toLowerCase() !== 'failed'
    : false;

  const { agent } = useGetSpecializedAgent(execution?.links?.agent || '');

  const { memoryChunks } = useGetAgentMemoryChunks(
    execution?.memory_id,
    execution?.links?.memory,
    isStreaming
  );

  const formatJsonForMarkdown = (obj: unknown): string => {
    return '```json\n' + JSON.stringify(obj, null, 2) + '\n```';
  };

  if (isLoadingExecution && !providedExecution) {
    return (
      <Flex flexDirection="column" gap="16" p="16">
        <Flex flexDirection="row" justifyContent="space-between" alignItems="center">
          <Text fontWeight="600" fontSize="lg">
            Loading Execution...
          </Text>
          <Button
            icon={<IconXmark size="16" />}
            appearance="default"
            onClick={() => {
              onClose();
            }}
            aria-label="Close sidebar"
          />
        </Flex>
        <Flex justifyContent="center" alignItems="center" h="224">
          <Text>Loading execution details...</Text>
        </Flex>
      </Flex>
    );
  }

  if (executionError || !execution) {
    return (
      <Flex flexDirection="column" gap="16" p="16">
        <Flex flexDirection="row" justifyContent="space-between" alignItems="center">
          <Text fontWeight="600" fontSize="lg">
            Execution Details
          </Text>
          <Button
            icon={<IconXmark size="16" />}
            appearance="default"
            onClick={onClose}
            aria-label="Close sidebar"
          />
        </Flex>
        <EmptyStateMessage message="Failed to load execution details" />
      </Flex>
    );
  }

  return (
    <ArtifactProvider>
      <ArtifactContainer>
        <Flex flexDirection="column" gap="16" px="16" pb="16">
          <Flex
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            className={styles.header}
          >
            <Flex flexDirection="row" alignItems="center" gap="12">
              <Box className={styles.iconContainer}>
                <IconAgent size="20" />
              </Box>
              <Text fontWeight="600" fontSize="lg">
                Agent Execution
              </Text>
              <ExecutionStatusBadge status={execution.status} showAsRunning />
            </Flex>
            <Flex flexDirection="row" gap="8" alignItems="center">
              {isStreaming && executionId && <StopExecutionButton executionId={executionId} />}
              <Button
                icon={<IconXmark size="16" />}
                appearance="subtle"
                onClick={onClose}
                aria-label="Close sidebar"
              />
            </Flex>
          </Flex>

          {/* Basic Info */}
          <ExecutionDetailsCard
            executionId={execution.id}
            status={execution.status}
            agentName={agent?.name ? `${agent.name}` : undefined}
            createdAt={execution.created_at}
            executionTime={execution.execution_time}
            tokenUsage={execution.token_usage}
            evaluationScore={execution.evaluation_score}
            baselineEvaluationScore={agent?.baseline_evaluation_score}
            variant="detailed"
            hideStatusBadge
            onNavigate={handleNavigate}
          />

          {/* Input Variables */}
          <Disclosure defaultOpen>
            <DisclosureTrigger>
              <Field label="Input Variables" info="Parameters used for this execution" />
            </DisclosureTrigger>
            <DisclosureContent>
              <Flex flexDirection="column" gap="16" className={styles.horizontalPadding}>
                {execution.parameters && Object.keys(execution.parameters).length > 0 ? (
                  <Field label="Variables">
                    <Box border="1" borderColor="border.default" rounded="md">
                      <Box bg="bg.default" p="12" rounded="md">
                        <MarkdownText text={formatJsonForMarkdown(execution.parameters)} />
                      </Box>
                    </Box>
                  </Field>
                ) : (
                  <EmptyStateMessage message="No parameters were used for this execution" />
                )}
              </Flex>
            </DisclosureContent>
          </Disclosure>

          {/* Artifact Disclosure */}
          {execution.artifacts && execution.artifacts.length > 0 && (
            <Disclosure w="full" defaultOpen>
              <DisclosureTrigger>
                <Field label="Artifacts" info="Agent Execution Artifacts:" />
              </DisclosureTrigger>
              <DisclosureContent>
                <Flex px="24">
                  <Artifacts artifacts={execution?.artifacts} />
                </Flex>
              </DisclosureContent>
            </Disclosure>
          )}
          {/* Output/Result */}
          <Disclosure defaultOpen>
            <DisclosureTrigger>
              <Field label="Output" info="Execution results and response" />
            </DisclosureTrigger>
            <DisclosureContent>
              <Flex flexDirection="column" gap="16" className={styles.horizontalPadding}>
                <Field label="Agent Response">
                  <ExecutionOutputDisplay
                    result={execution.result ? { response: execution.result.response } : null}
                    showStatus={false}
                    emptyMessage="No output available."
                    emptySubMessage={
                      isStreaming
                        ? 'Execution in progress.'
                        : 'Execution did not produce any results.'
                    }
                  />
                </Field>
              </Flex>
            </DisclosureContent>
          </Disclosure>

          {/* Execution Memory */}
          {execution.links?.memory && (
            <Disclosure defaultOpen>
              <DisclosureTrigger>
                <Field label="Execution Memory" info="Agent memory chunks from execution" />
              </DisclosureTrigger>
              <DisclosureContent>
                <Flex flexDirection="column" gap="16" className={styles.horizontalPadding}>
                  <MemoryChunksDisplay
                    memoryChunks={memoryChunks || []}
                    isStreaming={isStreaming}
                    emptyMessage="No memory chunks available"
                    emptySubMessage="Memory data will appear here when available."
                  />
                </Flex>
              </DisclosureContent>
            </Disclosure>
          )}

          {/* Quality Score */}
          {typeof execution.evaluation_score === 'number' && (
            <Box ref={qualityScoreRef}>
              <Disclosure open={qualityScoreOpen} onOpenChange={setQualityScoreOpen}>
                <DisclosureTrigger>
                  <Field
                    label={
                      <Flex flexDirection="row" alignItems="center" gap="8">
                        <Text>Quality Score: {execution.evaluation_score.toFixed(0)}%</Text>
                        <Badge
                          intent={
                            execution.evaluation_score >= (agent?.baseline_evaluation_score ?? 70)
                              ? 'success'
                              : 'danger'
                          }
                        >
                          {execution.evaluation_score >= (agent?.baseline_evaluation_score ?? 70)
                            ? 'Passed'
                            : 'Failed'}
                        </Badge>
                      </Flex>
                    }
                  />
                </DisclosureTrigger>
                <DisclosureContent>
                  <Flex flexDirection="column" gap="12" className={styles.horizontalPadding}>
                    {typeof agent?.baseline_evaluation_score === 'number' && (
                      <Flex flexDirection="row" alignItems="center" gap="4">
                        <Field
                          label="Baseline Quality"
                          info="The lowest acceptable quality score for this agent's response"
                        />
                        <Text>: {agent.baseline_evaluation_score.toFixed(0)}%</Text>
                      </Flex>
                    )}
                    {execution.evaluation_feedback && (
                      <Field label="Evaluation Feedback">
                        <Text>{execution.evaluation_feedback}</Text>
                      </Field>
                    )}
                  </Flex>
                </DisclosureContent>
              </Disclosure>
            </Box>
          )}

          {/* Token Usage */}
          <TokenUsageSection
            tokenUsage={execution.token_usage}
            ref={tokenUsageRef}
            open={tokenUsageOpen}
            onOpenChange={setTokenUsageOpen}
          />
        </Flex>
      </ArtifactContainer>
    </ArtifactProvider>
  );
}
